import { Landmark, ClipboardX, FileInput, PencilLine } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useSuspenseQuery } from '@apollo/client/react';
import { Fragment, useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { LegalContextEnum, AgreementConditionEnum, SuggestedChangeChangeTypeEnum } from 'src/graphql/schema';
import { SuggestedChangesDocument } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { Button } from '@/components/ui/button';
import { SuggestedChangesView } from 'src/components/SuggestChanges';
import { getAgreementSuggestedChangesItems, RequestChangesTooltip } from 'src/containers/SoftwareEscrow/RequestChanges';
import { ReviewChangesDialog } from 'src/containers/SoftwareEscrow/RequestChanges/Agreement';
import { SummaryCard } from '@/components/ui/summary-card';
import { legalContextMap, releaseEventsMap, jurisdictionMap, liabilityMap } from 'src/containers/Escrow/Agreement';

import type { ISuggestedChangesQuery } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';

const RequestChangesActions = ({
  suggestedChange,
}: {
  suggestedChange?: ISuggestedChangesQuery['suggestedChanges']['nodes'][0];
}) => {
  const navigate = useNavigate();
  const {
    softwareEscrow: { id, policy },
  } = useSoftwareEscrow();

  const onRequestChanges = () => navigate(`/software-escrows/${id}/request-changes/agreement`);

  const actions = useMemo(
    () => [
      {
        id: 'review',
        component: <ReviewChangesDialog suggestedChangeId={suggestedChange?.id || ''} />,
        condition: suggestedChange?.policy.review,
      },
      {
        id: 'requestChanges',
        component: <RequestChangesTooltip />,
        condition: suggestedChange,
      },
      {
        id: 'createAgreementSuggestedChange',
        component: (
          <Button variant="outline" onClick={onRequestChanges}>
            <PencilLine />
            Request changes
          </Button>
        ),
        condition: policy.createAgreementSuggestedChange,
      },
    ],
    [suggestedChange, policy, onRequestChanges],
  );

  const actionsFiltered = actions.filter((action) => action.condition);

  if (!actionsFiltered.length) return null;

  return (
    <div className="flex justify-end gap-3 mb-8">
      {actionsFiltered.map(({ id, component }) => (
        <Fragment key={id}>{component}</Fragment>
      ))}
    </div>
  );
};

const AgreementViewContainer = () => {
  const {
    softwareEscrow: { id, agreement },
  } = useSoftwareEscrow();
  const { data: suggestedChangesData } = useSuspenseQuery(SuggestedChangesDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      escrowId: id,
      changeType: SuggestedChangeChangeTypeEnum.AgreementSuggestedChange,
    },
    skip: agreement?.thirdPartyAgreement,
  });

  if (agreement?.thirdPartyAgreement) {
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg">Agreement configuration</h2>
          <p className="text-muted-foreground text-sm">
            The specifics of the document containing the legal terms of the escrow.
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Agreement template</CardTitle>
            <CardDescription>The legal foundation of your escrow agreement.</CardDescription>
          </CardHeader>
          <CardContent>
            <SummaryCard
              title="External escrow agreement template"
              icon={FileInput}
              description="Remember to send your escrow agreement template to Codekeeper at service@codekeeper.co"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  const suggestedChangesPayload = suggestedChangesData?.suggestedChanges?.nodes?.[0]?.payload;

  return (
    <div className="space-y-4">
      <RequestChangesActions suggestedChange={suggestedChangesData?.suggestedChanges?.nodes?.[0]} />
      <div className="space-y-1">
        <h2 className="text-lg">Agreement configuration</h2>
        <p className="text-muted-foreground text-sm">
          The specifics of the document containing the legal terms of the escrow.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Legal context</CardTitle>
          <CardDescription>
            The title of the agreement between you and your counterpart, as referenced in the escrow agreement.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SuggestedChangesView
            items={[
              ...(suggestedChangesPayload?.legal_context
                ? [getAgreementSuggestedChangesItems('context', suggestedChangesPayload.legal_context)]
                : []),
              ...(suggestedChangesPayload?.legal_context_other
                ? [getAgreementSuggestedChangesItems('otherContext', suggestedChangesPayload.legal_context_other)]
                : []),
            ]}
          />
          {Boolean(agreement?.legalContext.length) && (
            <div className="space-y-2">
              {agreement?.legalContext.map((context) => {
                const { title, icon, description } = legalContextMap[context];
                let cardTitle = title;
                if (context === LegalContextEnum.Other) cardTitle = `${agreement?.legalContextOther || ''} Agreement`;

                return <SummaryCard key={context} title={cardTitle} icon={icon} description={description} />;
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Release events</CardTitle>
          <CardDescription>
            The specific conditions under which the deposited assets will be released to the beneficiary.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SuggestedChangesView
            items={[
              ...(suggestedChangesPayload?.conditions
                ? [getAgreementSuggestedChangesItems('conditions', suggestedChangesPayload.conditions)]
                : []),
              ...(suggestedChangesPayload?.conditions_other
                ? [getAgreementSuggestedChangesItems('conditionsOther', suggestedChangesPayload.conditions_other)]
                : []),
            ]}
          />
          {Boolean(agreement?.conditions.length) && (
            <div className="space-y-2">
              {agreement?.conditions.map((condition) => {
                const { title, icon, description } = releaseEventsMap[condition];
                let cardTitle = title;
                if (condition === AgreementConditionEnum.Other) cardTitle = agreement?.conditionsOther || '';

                return <SummaryCard key={condition} title={cardTitle} icon={icon} description={description} />;
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Jurisdiction</CardTitle>
          <CardDescription>
            This determines which country&apos;s or region&apos;s laws will apply in the event of any legal disputes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SuggestedChangesView
            items={[
              ...(suggestedChangesPayload?.jurisdiction
                ? [getAgreementSuggestedChangesItems('jurisdiction', suggestedChangesPayload.jurisdiction)]
                : []),
            ]}
          />
          <SummaryCard
            title={agreement?.jurisdiction ? jurisdictionMap[agreement?.jurisdiction] : ''}
            icon={Landmark}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Escrow Agreement Liability SLA</CardTitle>
          <CardDescription>
            The total aggregate liability limit for the escrow agent as specified in the agreement.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SuggestedChangesView
            items={[
              ...(suggestedChangesPayload?.liability
                ? [getAgreementSuggestedChangesItems('liability', suggestedChangesPayload.liability)]
                : []),
            ]}
          />
          <SummaryCard
            title={agreement?.liability ? liabilityMap[agreement?.liability] : ''}
            icon={ClipboardX}
            description="Non-standard limits require the selected Liability SLA."
            descriptionStyle="italic"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AgreementViewContainer;
